// The roadmap's navigation: five sections on one row, and under a section that
// has several surfaces a smaller row of its views. Replaces the single row of
// ten tabs that clipped on a laptop (owner review 4jsvwe, visual reviewer:
// "cut ten tabs to four"). Both rows are ARIA tablists; a section is selected
// when the surface on screen belongs to it.

import { TAB_LABELS, groupBadge, groupOf, tabBadge, visibleGroups, type RoadmapTab, type TabBadgeTone } from './roadmapTabs';
import styles from './RoadmapV2.module.css';

interface Props {
  tab: RoadmapTab;
  onTab: (t: RoadmapTab) => void;
  counts: Record<RoadmapTab, number>;
  /** Surfaces not drawn for this reader (roadmapTabs.LEADERSHIP_TABS for a non-leadership reader). */
  hidden?: ReadonlySet<RoadmapTab>;
}

function Pill({ badge }: { badge: { text: string; tone: TabBadgeTone } | null }) {
  if (!badge) return null;
  return (
    <span className={`${styles.navPill} ${badge.tone === 'alert' ? styles.navPillAlert : ''}`} data-tone={badge.tone}>
      {badge.text}
    </span>
  );
}

export function RoadmapNav({ tab, onTab, counts, hidden }: Props) {
  const current = groupOf(tab, hidden);
  return (
    <nav className={styles.nav} aria-label="Roadmap sections">
      <div className={styles.navMain} role="tablist" aria-label="Roadmap sections">
        {visibleGroups(hidden).map((g) => (
          <button
            key={g.id}
            type="button"
            role="tab"
            aria-selected={g.id === current.id}
            // The accessible name is the label alone: the recorder records a
            // tab by its name, and the pill's count made "Initiatives 14" a
            // different step from "Initiatives 15" (kanban 68b23ba8).
            aria-label={g.label}
            data-action="roadmap_section_tab"
            className={`${styles.navTab} ${g.id === current.id ? styles.navTabOn : ''}`}
            onClick={() => onTab(g.id === current.id ? tab : g.tabs[0])}
          >
            {g.label}
            <Pill badge={groupBadge(g, counts)} />
          </button>
        ))}
      </div>
      {current.tabs.length > 1 && (
        <div className={styles.navSub} role="tablist" aria-label={`${current.label} views`}>
          {current.tabs.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={t === tab}
              aria-label={TAB_LABELS[t]}
              data-action="roadmap_view_tab"
              className={`${styles.navSubTab} ${t === tab ? styles.navSubTabOn : ''}`}
              onClick={() => onTab(t)}
            >
              {TAB_LABELS[t]}
              {/* The list's count already sits on the section tab above. */}
              {t !== current.tabs[0] || current.id === 'more' ? <Pill badge={tabBadge(t, counts[t])} /> : null}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}
